import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SnapshotModal.css';

export type SnapshotShareAction = 'download' | 'copy_image' | 'native_share' | 'copy_link';

interface SnapshotModalProps {
    blob: Blob;
    fileName: string;
    shareTitle: string;
    shareText: string;
    shareUrl: string;
    onClose: () => void;
    /** Invoked when the user completes a share action (for analytics). */
    onAction?: (action: SnapshotShareAction) => void;
}

const SnapshotModal: React.FC<SnapshotModalProps> = ({
    blob,
    fileName,
    shareTitle,
    shareText,
    shareUrl,
    onClose,
    onAction,
}) => {
    const { t } = useTranslation();
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [note, setNote] = useState<string | null>(null);

    useEffect(() => {
        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [blob]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const flash = (msg: string) => {
        setNote(msg);
        window.setTimeout(() => setNote(null), 2500);
    };

    const file = useMemo(
        () => new File([blob], fileName, { type: blob.type || 'image/png' }),
        [blob, fileName],
    );

    const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

    const canCopyImage =
        typeof window !== 'undefined' &&
        typeof (window as any).ClipboardItem !== 'undefined' &&
        !!navigator.clipboard &&
        typeof navigator.clipboard.write === 'function';

    const download = () => {
        const a = document.createElement('a');
        a.href = objectUrl ?? URL.createObjectURL(blob);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        flash(t('snapshot.downloaded'));
        onAction?.('download');
    };

    const copyImage = async () => {
        try {
            const ClipboardItemCtor = (window as any).ClipboardItem;
            await navigator.clipboard.write([
                new ClipboardItemCtor({ [blob.type || 'image/png']: blob }),
            ]);
            flash(t('snapshot.copiedImage'));
            onAction?.('copy_image');
        } catch {
            flash(t('snapshot.copyImageFailed'));
        }
    };

    const nativeShare = async () => {
        try {
            await navigator.share({ files: [file], title: shareTitle, text: shareText, url: shareUrl });
            onAction?.('native_share');
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            flash(t('snapshot.shareFailed'));
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            flash(t('snapshot.linkCopied'));
            onAction?.('copy_link');
        } catch {
            flash(shareUrl);
        }
    };

    return (
        <div className="empire-modal-overlay empire-modal-overlay--z-low snap-share-overlay" onClick={onClose}>
            <div className="empire-modal empire-modal--wide empire-modal--structured" onClick={(e) => e.stopPropagation()}>
                <div className="empire-modal-head">
                    <h2>{t('snapshot.title')}</h2>
                    <button type="button" className="empire-modal-close" onClick={onClose} aria-label={t('common.close')}>✕</button>
                </div>

                <div className="snap-preview">
                    {objectUrl && <img src={objectUrl} alt={t('snapshot.previewAlt')} />}
                </div>

                <div className="snap-actions">
                    <button type="button" className="empire-btn empire-btn--solid" onClick={download}>
                        ⬇ {t('common.download')}
                    </button>
                    {canCopyImage && (
                        <button type="button" className="empire-btn" onClick={copyImage}>
                            ⧉ {t('snapshot.copyImage')}
                        </button>
                    )}
                    {canShareFiles && (
                        <button type="button" className="empire-btn" onClick={nativeShare}>
                            ↗ {t('common.share')}
                        </button>
                    )}
                    <button type="button" className="empire-btn" onClick={copyLink}>
                        🔗 {t('snapshot.copyLink')}
                    </button>
                </div>

                {note && <div className="snap-note">{note}</div>}
            </div>
        </div>
    );
};

export default SnapshotModal;
